/**
 * One curated global: what it is, how it varies, what it looks like.
 *
 * The identity block is the only place in this app where saving can change a **version**, and
 * that is worth the confirm it gets: an identity edit re-addresses the base reference, so the
 * picture already on the shared shelf stops being this piece's and has to be drawn and
 * approved again. Renames, descriptions and axes leave it alone — `applyPrimitivePatch` owns
 * that rule, and this surface just asks first when the rule says it will bump.
 *
 * **There is no permanent delete.** Archiving is the take-down: the piece leaves Browse, and
 * anything already using it keeps resolving and keeps rendering. A curated publication
 * outlives whoever filed it, and the reference check a real deletion would need is a callable
 * scoped to an author's own library, which this is not.
 */

import {
  Button,
  Dialog,
  EmptyState,
  Field,
  SectionHeader,
  SurfaceHeader,
  TextAreaField,
  TextLink,
} from '@atlas-nook/design-system';
import { DEFAULT_THEME_ID } from '@atlas-nook/brand';
import { patchTouchesIdentity, variantInstanceId } from '@atlas-nook/story';
import type { Localized, Primitive, PrimitivePatch } from '@atlas-nook/story';
import { useState } from 'react';
import type { JSX } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { useGlobal, useVariantInstance } from '../data/queries.js';
import { archiveGlobal, patchGlobal, restoreGlobal } from '../data/writes.js';
import { useToast } from '../shell/ToastProvider.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { PRIMITIVE_KIND } from '../catalog/options.js';
import { GlobalAxes } from './GlobalAxes.js';
import { GlobalMatrix } from './GlobalMatrix.js';
import { PreRenderPanel, referenceStateOf } from './PreRenderPanel.js';

export function GlobalDetail(): JSX.Element {
  const { id } = useParams();
  const { data: piece, loading } = useGlobal(id);

  if (id === undefined) return <Navigate to="/globals" replace />;
  if (piece === null) return loading ? <></> : <EmptyState title={S.global.notFound} />;
  return <GlobalBody key={`${piece.id}:${piece.version}`} piece={piece} />;
}

/** One localized text field, as a plain EN box with an optional TR box under it. */
function LocalizedPair({
  label,
  help,
  value,
  multiline,
  onChange,
}: {
  label: string;
  help?: string;
  value: Localized | undefined;
  multiline?: boolean;
  onChange: (next: Localized | undefined) => void;
}): JSX.Element {
  const en = value?.en ?? '';
  const tr = value?.tr ?? '';
  const Control = multiline === true ? TextAreaField : Field;

  function next(nextEn: string, nextTr: string): Localized | undefined {
    if (nextEn.trim() === '' && nextTr.trim() === '') return undefined;
    return nextTr.trim() === '' ? { en: nextEn } : { en: nextEn, tr: nextTr };
  }

  return (
    <div className="lpair">
      <Control
        label={`${label} · ${S.common.en}`}
        {...(help === undefined ? {} : { help })}
        {...(multiline === true ? { rows: 4 } : {})}
        value={en}
        onChange={(value_: string) => onChange(next(value_, tr))}
      />
      <Control
        label={`${label} · ${S.common.tr}`}
        {...(multiline === true ? { rows: 4 } : {})}
        value={tr}
        onChange={(value_: string) => onChange(next(en, value_))}
      />
    </div>
  );
}

function GlobalBody({ piece }: { piece: Primitive }): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();

  const [draft, setDraft] = useState<PrimitivePatch>({});
  const [confirmingBump, setConfirmingBump] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [working, setWorking] = useState(false);

  const { data: base } = useVariantInstance(
    variantInstanceId(piece.id, piece.version, {}, DEFAULT_THEME_ID),
  );
  const referenceState = referenceStateOf(piece, base);
  const archived = piece.archivedAt !== null && piece.archivedAt !== undefined;

  const merged: Primitive = { ...piece, ...draft };
  const dirty = Object.keys(draft).length > 0;
  const bumps = patchTouchesIdentity(piece, draft);

  function set<K extends keyof PrimitivePatch>(key: K, value: PrimitivePatch[K]): void {
    setDraft((was) => ({ ...was, [key]: value }));
  }

  async function save(): Promise<void> {
    setWorking(true);
    try {
      const result = await patchGlobal(piece, draft);
      setDraft({});
      setConfirmingBump(false);
      toast.show(result.bumped ? S.global.bumped(result.version) : S.global.savedNoBump);
    } catch {
      toast.show(S.common.failed);
    } finally {
      setWorking(false);
    }
  }

  return (
    <>
      <SurfaceHeader
        kicker={`${S.global.kicker} · ${PRIMITIVE_KIND[piece.kind]}`}
        title={piece.name.en}
        meta={`${S.global.version(piece.version)}${archived ? ` · ${S.globals.archivedTag}` : ''}`}
        actions={
          <>
            <TextLink onClick={() => void navigate('/globals')}>{S.nav.globals}</TextLink>
            {archived ? (
              <Button tone="secondary" onClick={() => void restoreGlobal(piece.id)}>
                {S.global.restore}
              </Button>
            ) : (
              <Button tone="quiet" onClick={() => setArchiving(true)}>
                {S.global.archive}
              </Button>
            )}
          </>
        }
      />

      <section className="fsec">
        <SectionHeader title={S.global.identity} />
        <div className="stack">
          <p className="note">{S.global.identityHelp}</p>

          <LocalizedPair
            label={S.globals.nameEn.replace(' · English', '')}
            value={merged.name}
            onChange={(value) => set('name', value ?? { en: '' })}
          />
          <LocalizedPair
            label={S.global.description}
            multiline
            value={merged.description}
            onChange={(value) => set('description', value)}
          />
          <LocalizedPair
            label={S.global.identityNotes}
            help={S.global.identityNotesHelp}
            multiline
            value={merged.identityNotes}
            onChange={(value) => set('identityNotes', value)}
          />
          <LocalizedPair
            label={S.global.colourAnchor}
            help={S.global.colourAnchorHelp}
            value={merged.colourAnchor}
            onChange={(value) => set('colourAnchor', value)}
          />

          <div className="btnrow">
            <Button
              tone="primary"
              isDisabled={!dirty}
              working={working}
              onClick={() => (bumps ? setConfirmingBump(true) : void save())}
            >
              {working ? S.common.saving : S.common.save}
            </Button>
            {/* Three words beside the button; the confirm says the whole of it. */}
            {dirty && bumps && <span className="cap">{S.global.bumpsVersion}</span>}
          </div>
        </div>
      </section>

      <section className="fsec">
        <SectionHeader title={S.global.reference} />
        <PreRenderPanel primitive={piece} />
      </section>

      <section className="fsec">
        <SectionHeader title={S.global.axes} />
        <GlobalAxes primitive={piece} />
      </section>

      <section className="fsec">
        <GlobalMatrix primitive={piece} allowed={referenceState === 'ready'} />
      </section>

      {/* A sentence, so `.note` rather than `.cap` — a caption style shouts it in caps. */}
      <p className="note">{S.global.noDelete}</p>

      <Dialog
        open={confirmingBump}
        onClose={() => setConfirmingBump(false)}
        title={S.global.bumpTitle}
        actions={
          <>
            <Button tone="quiet" onClick={() => setConfirmingBump(false)}>
              {S.common.cancel}
            </Button>
            <Button tone="primary" working={working} onClick={() => void save()}>
              {S.global.bumpConfirm}
            </Button>
          </>
        }
      >
        <p className="note">{S.global.bumpBody}</p>
      </Dialog>

      <Dialog
        open={archiving}
        onClose={() => setArchiving(false)}
        title={S.global.archiveTitle(piece.name.en)}
        actions={
          <>
            <Button tone="quiet" onClick={() => setArchiving(false)}>
              {S.common.cancel}
            </Button>
            <Button
              tone="primary"
              onClick={() => {
                void archiveGlobal(piece.id).then(() => {
                  setArchiving(false);
                  toast.show(S.global.archived);
                });
              }}
            >
              {S.global.archiveConfirm}
            </Button>
          </>
        }
      >
        <p className="note">{S.global.archiveBody}</p>
      </Dialog>
    </>
  );
}
